import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as uuid from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {InaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {
  CREATE_PROCESS_MODAL_TYPE,
  UPDATE_FLOWCHART_MODAL_TYPE,
} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  CloseType,
  WORKFLOWS_ANALYTICS_ACTIONS,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {FormActionConfigInput} from 'forms/form-designer/Constants';
import {CREATE_TASK_ACTION} from 'forms/form-designer/FormDesigner';
import {FieldKeyToTableColumnIndex, FormInstanceTableState} from 'forms/form-instance/Constants';
import {FormInstanceContents} from 'forms/form-instance/FormInstanceContents';
import {
  applyAffectedTableColumnFixes,
  makeAffectedTableColumnWarnings,
  determineAffectedTableColumnIndexes,
} from 'forms/form-instance/FormInstanceTableState';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingQueryResult, QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useSelectorsById, {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {
  DefaultValuesByColumnType,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOrFormOutsideNotebook, {
  FORM_ROW_LIMIT,
  TableOrFormRenderOptions,
  TableOrFormRenderOptionsType,
} from 'notebook/structured-tables/StructuredTableOrFormOutsideNotebook';
import TableFormSelector, {
  TABLE_LOCKED_TABLE_FORM_TOGGLE_BUTTON_OPTIONS,
  TABLE_FORM_TOGGLE_BUTTON_OPTIONS,
} from 'notebook/structured-tables/TableFormSelector';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {MaximumTableHeight} from 'notebook/tables/TableConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  createProcessEventMetadata,
  PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT,
  PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT,
} from 'performance/processProcessesEvents';
import preferences from 'Preferences';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {createProcess} from 'processes/dao/ProcessManagementDAO';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import areProcessInteractiveFlowchartsDifferent from 'processes/graph-builder-processes/flowchart-viz/util/areProcessInteractiveFlowchartsDifferent';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {SchemaMapperConfig} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  DefaultTaskGroupInstructionsLines,
  EVALUATED_TABLE_ERROR_OPTIONS,
  ProcessTableLocation,
} from 'processes/ProcessConstants';
import {
  buildExecutionFlowchartSpecFromDraftState,
  ExecutionFlowchartSpecVariant,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/buildExecutionFlowchartSpecFromDraftState';
import type {ExecutionFlowchartReadinessSnapshot} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPaneTypes';
import {useExecutionFlowchartDraftStateForConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/useExecutionFlowchartDraftState';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {
  hasBlockingExecutionErrors,
  validateFlowchartDataForExecution,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartValidationAndAutosaverControl';
import createEmptyTaskTableFromSchema from 'processes/tables/createEmptyTaskTableFromSchema';
import createOutputTableWithPlaceholderRowFromSchema from 'processes/tables/createOutputTableWithPlaceholderRowFromSchema';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import {TaskGroupInstructions} from 'processes/TaskGroupInstructions';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import EntityStore from 'stores/EntityStore';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_LIGHTER, WHITE, BLUE_LIGHT} from 'ui-library/Colors';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import Modal, {type ModalButton} from 'ui-library/modals/Modal';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import ToggleGroup from 'ui-library/ToggleGroup';
import {confirm} from 'util/Modals';
import {assertNever} from 'util/Types';

import convertProcessInteractiveFlowchartToFlowchartSpec from '../graph-builder-processes/flowchart-viz/util/convertProcessInteractiveFlowchartToFlowchartSpec';
import {ProcessTaskSchemaQuery, ProcessTaskSchemaQueryVariables} from './__generated/ProcessTaskSchemaQuery';
import {ProcessTaskSchemaWithFormsQuery} from './__generated/ProcessTaskSchemaWithFormsQuery';
import {
  initializeCreateProcessModalFormInstanceTableState,
  initializeCreateProcessModalFieldKeyToTableColumnIndex,
  initializeCreateProcessModalFormInstanceComputedTableState,
} from './CreateProcessFormInstanceState';
import CreateProcessGraphBuilderPane from './CreateProcessGraphBuilderPane';
import {
  CreateTaskModalViewPreference,
  getInitialCreateTaskModalViewPreference,
  saveCreateTaskModalViewPreference,
} from './CreateProcessModalViewPreference';
import ImprovedCreateProcessFlowchartPane from './ImprovedCreateProcessFlowchartPane';

type ProcessTaskSchema = ExtractByTypename<
  ProcessTaskSchemaWithFormsQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

type FormDefinition = ProcessTaskSchemaWithFormsQuery['formDefinitions'][number];

function getCreateProcessModalFlowchartEligibility(taskSchema: ProcessTaskSchema | null | undefined): {
  isFlowchartTooLarge: boolean;
  canShowFlowchartPane: boolean;
  flowchartConfigIdForAcmDraft: string | null;
} {
  if (taskSchema == null) {
    return {
      isFlowchartTooLarge: false,
      canShowFlowchartPane: false,
      flowchartConfigIdForAcmDraft: null,
    };
  }

  const currentFccv = taskSchema.flowchartConfig?.currentFlowchartConfigVersion;
  const accessibleFccv = currentFccv != null && !isInaccessible(currentFccv) ? currentFccv : null;
  const isFlowchartTooLarge =
    (accessibleFccv?.templateFlowchart?.numNodes ?? 0) >
    BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI;

  const canShowFlowchartPane =
    taskSchema.executionType === ProcessExecutionType.FLOWCHART &&
    accessibleFccv != null &&
    !isFlowchartTooLarge;

  let flowchartConfigIdForAcmDraft: string | null = null;
  if (
    BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG &&
    taskSchema.executionType === ProcessExecutionType.FLOWCHART &&
    accessibleFccv != null &&
    !isFlowchartTooLarge &&
    taskSchema.flowchartConfig != null &&
    !isInaccessible(taskSchema.flowchartConfig)
  ) {
    flowchartConfigIdForAcmDraft = taskSchema.flowchartConfig.id;
  }

  return {
    isFlowchartTooLarge,
    canShowFlowchartPane,
    flowchartConfigIdForAcmDraft,
  };
}

export const PROCESS_TASK_SCHEMA_DETAILS_QUERY = gql`
  query ProcessTaskSchemaQuery($processTaskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $processTaskSchemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        flowchartConfig {
          id
          currentFlowchartConfigVersion {
            ... on ProcessFlowchartConfigVersionNode {
              id
              isFlowchartModifiableOnCreation
              templateFlowchart {
                id
                numNodes
              }
              doesFlowchartConfigUseTaskDefinitions
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCESS_TASK_SCHEMA_DETAILS_WITH_FORMS_QUERY = gql`
  query ProcessTaskSchemaWithFormsQuery($processTaskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $processTaskSchemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        flowchartConfig {
          id
          currentFlowchartConfigVersion {
            ... on ProcessFlowchartConfigVersionNode {
              id
              isFlowchartModifiableOnCreation
              templateFlowchart {
                id
                numNodes
              }
              doesFlowchartConfigUseTaskDefinitions
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    formDefinitions(schemaId: $processTaskSchemaId) {
      ... on FormDefinitionNode {
        id
        name
        latestFormDefinitionVersion {
          id
          definition {
            sections
          }
          actionConfigs {
            actionId
            sectionKey
            parameters {
              formFieldKey
              actionParameterId
            }
          }
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const stubbableFunctions = {
  makeClientId: (): string => uuid.v4(),
};

export const TableOrFormAdjustmentType = {
  SWITCHED_TO_TASK_TABLE_VIEW: 'switched_to_task_table_view',
  SWITCHED_TO_TASK_FORM_VIEW: 'switched_to_task_form_view',
} as const;

export enum TableOrFormAnalyticsType {
  FORM_VIEW = 'form_view',
  TABLE_VIEW = 'table_view',
}

export const TableOrFormRenderOptionsToAnalyticsType = {
  [TableOrFormRenderOptions.FORM]: TableOrFormAnalyticsType.FORM_VIEW,
  [TableOrFormRenderOptions.TABLE]: TableOrFormAnalyticsType.TABLE_VIEW,
} as const;

function useProcessTaskSchema(processTaskSchemaId: string): ProcessQueryResult {
  const queryResult = useBenchlingQuery<
    ProcessTaskSchemaQuery | ProcessTaskSchemaWithFormsQuery,
    ProcessTaskSchemaQueryVariables
  >(
    isWorkflowCreateTaskFormsEnabled()
      ? PROCESS_TASK_SCHEMA_DETAILS_WITH_FORMS_QUERY
      : PROCESS_TASK_SCHEMA_DETAILS_QUERY,
    {
      variables: {processTaskSchemaId},
      fetchPolicy: 'cache-and-network',
    }
  ) as BenchlingQueryResult<ProcessTaskSchemaWithFormsQuery>;
  if (queryResult.type === QueryResultType.SUCCESS) {
    if (isInaccessible(queryResult.data.processTaskSchema)) {
      return {
        type: queryResult.type,
        taskSchema: null,
        configuredForms: null,
        inaccessibleInfo: queryResult.data.processTaskSchema,
      };
    }
    const taskSchema = queryResult.data.processTaskSchema;
    const configuredForms = queryResult.data.formDefinitions ?? null;
    return {
      type: queryResult.type,
      taskSchema,
      configuredForms,
    };
  }
  return {type: queryResult.type, taskSchema: null, configuredForms: null};
}

function useSelectorsByIdWithStatus(): SelectorsByIdResult {
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});
  const isSuccess = selectorsById !== AsyncStatus.LOADING && selectorsById !== AsyncStatus.ERROR;
  return {
    type: isSuccess ? AsyncStatus.SUCCESS : selectorsById,
    selectorsById: isSuccess ? selectorsById : null,
  } as SelectorsByIdResult;
}

export interface ProcessQueryResult {
  type: QueryResultType;
  taskSchema: ProcessTaskSchema | null;
  configuredForms: Array<FormDefinition> | null;
  inaccessibleInfo?: InaccessibleInfo;
}

interface SelectorsByIdResult {
  type: AsyncStatus;
  selectorsById: SchemaFieldSelectorsDict | null;
}

interface CreateProcessModalContentProps {
  schemaQueryResult: ProcessQueryResult;
  selectorsByIdResult: SelectorsByIdResult;
  folderId: string | null;
  setFolderId: (folderId: string) => void;
  watchers: Array<EntityWithTextAndSuspended>;
  setWatchers: (watchers: Array<EntityWithTextAndSuspended>) => void;
  taskGroupInstructions: string | null;
  taskTable?: ProcessTaskStructuredTable;
  setTaskTable: (table: ProcessTaskStructuredTable) => void;
  computedTaskTable: EvaluatedTable;
  setComputedTaskTable: (computedTable: EvaluatedTable) => void;
  taskTableOrFormToggleGroup: TableOrFormRenderOptionsType;
  setTaskTableOrFormToggleGroup: (taskTableOrFormToggleGroup: TableOrFormRenderOptionsType) => void;
  selectedForm?: FormDefinition;
  setSelectedForm: (form: FormDefinition) => void;
}

interface CreateProcessFormProps extends Omit<
  CreateProcessModalContentProps,
  'schemaQueryResult' | 'selectorsByIdResult'
> {
  taskSchema: ProcessTaskSchema;
  configuredForms: Array<FormDefinition> | null;
  selectorsById: {[id: string]: SchemaFieldSelector};
}

interface CreateProcessModalProps {
  processTaskSchemaId: string;
  initialFolderId: string | null;
  taskGroupInstructions: string | null;
  processTaskSchemaName: string;
  onRequestClose: () => void;
  sourceType: string;
  analyticsEventGroupId: string;
  taskTableWithPropagatedContent?: ProcessTaskStructuredTable;
  footerMessage?: string;
  defaultTaskValuesByColumnType?: DefaultValuesByColumnType;
}

interface EmbeddedStructuredTableProps {
  taskSchema: ProcessTaskSchema;
  structuredTable: UnknownStructuredTable;
  updateHandler: (structuredTable: UnknownStructuredTable) => void;
  computedTable?: EvaluatedTable;
  onComputedTableChanged?: (computedTable: EvaluatedTable) => void;
  locked?: boolean;
  taskTableOrFormToggleGroup: TableOrFormRenderOptionsType;
  setTaskTableOrFormToggleGroup?: (taskTableOrFormToggleGroup: TableOrFormRenderOptionsType) => void;
  selectedForm?: FormDefinition;
  setSelectedForm: (form: FormDefinition | undefined) => void;
  forms?: Array<FormDefinition>;
  isOutputTable?: boolean;
}

interface EmbeddedCustomFormProps {
  tableState: FormInstanceTableState;
  fieldKeyToTableColumnIndex: FieldKeyToTableColumnIndex;
  updateHandler: (structuredTable: UnknownStructuredTable) => void;
  onComputedTableChanged?: (computedTable: EvaluatedTable) => void;
  selectedForm: FormDefinition;
}

export function EmbeddedCustomForm({
  tableState,
  fieldKeyToTableColumnIndex,
  onComputedTableChanged,
  updateHandler,
  selectedForm,
}: EmbeddedCustomFormProps): JSX.Element {
  return (
    <FormInstanceContents
      formDefinitionContent={selectedForm.latestFormDefinitionVersion.definition}
      formInstanceTableState={tableState}
      onUpdateFormInstanceTableState={(state) => {
        if (state.hasOwnProperty(CREATE_TASK_ACTION.identifier)) {
          updateHandler(
            produce(tableState[CREATE_TASK_ACTION.identifier], (draftTable) => {
              draftTable.rows = state[CREATE_TASK_ACTION.identifier].rows;
            })
          );
        }
      }}
      fieldKeyToTableColumnIndex={fieldKeyToTableColumnIndex}
      onUpdateFormInstanceComputedTableState={(state) => {
        if (state.hasOwnProperty(CREATE_TASK_ACTION.identifier)) {
          if (onComputedTableChanged) {
            onComputedTableChanged(state[CREATE_TASK_ACTION.identifier]);
          }
        }
      }}
    />
  );
}

export function EmbeddedStructuredTable({
  taskSchema,
  structuredTable,
  setTaskTableOrFormToggleGroup,
  taskTableOrFormToggleGroup,
  computedTable,
  onComputedTableChanged,
  updateHandler,
  locked,
  selectedForm,
  setSelectedForm,
  forms,
  isOutputTable,
}: EmbeddedStructuredTableProps): JSX.Element {
  /** Stores the starting state of the StructuredTable to remember column definitions that might be affected
   * by forms.
   */
  const initialStructuredTable = useRef(structuredTable);
  const showToggleGroup = setTaskTableOrFormToggleGroup !== undefined && isWorkflowCreateTaskFormsEnabled();

  /** prevents using form view when there are too many rows in the table */
  const tableLockEnabled =
    taskTableOrFormToggleGroup === TableOrFormRenderOptions.TABLE &&
    structuredTable.rows.length >= FORM_ROW_LIMIT;

  const showConfiguredForm =
    isWorkflowCreateTaskFormsEnabled() &&
    taskTableOrFormToggleGroup === TableOrFormRenderOptions.FORM &&
    Boolean(selectedForm);

  const fieldKeyToTableColumnIndex = useMemo(() => {
    if (!selectedForm) {
      return undefined;
    }
    // Configured Forms for the Create Process Modal must have 1 (and only 1) create task action
    const actionConfigs: Array<FormActionConfigInput> =
      selectedForm.latestFormDefinitionVersion.actionConfigs.map((actionConfig) => ({
        identifier: actionConfig.actionId,
        actionVersion: -1,
        parameters: actionConfig.parameters,
      }));
    return initializeCreateProcessModalFieldKeyToTableColumnIndex(
      structuredTable as ProcessTaskStructuredTable,
      actionConfigs
    );
  }, [selectedForm, structuredTable]);

  const tableState = useMemo(() => {
    if (!selectedForm) {
      return undefined;
    }
    return initializeCreateProcessModalFormInstanceTableState(
      structuredTable as ProcessTaskStructuredTable,
      selectedForm.latestFormDefinitionVersion.definition,
      fieldKeyToTableColumnIndex ?? {}
    );
  }, [fieldKeyToTableColumnIndex, selectedForm, structuredTable]);

  const computedTableState = useMemo(
    () => initializeCreateProcessModalFormInstanceComputedTableState(computedTable),
    [computedTable]
  );

  const onTableFormSelect = useCallback(
    (selectedOption: TableOrFormRenderOptionsType, selectedForm?: FormDefinition) => {
      saveCreateTaskModalViewPreference(taskSchema.id, [selectedOption, selectedForm?.id]);
      if (selectedOption === TableOrFormRenderOptions.FORM && selectedForm && tableState) {
        const actionConfigs: Array<FormActionConfigInput> =
          selectedForm.latestFormDefinitionVersion.actionConfigs.map((actionConfig) => ({
            identifier: actionConfig.actionId,
            actionVersion: -1,
            parameters: actionConfig.parameters,
          }));
        const newFieldKeyToTableColumnIndex = initializeCreateProcessModalFieldKeyToTableColumnIndex(
          structuredTable as ProcessTaskStructuredTable,
          actionConfigs
        );
        const affectedColumnIndexes = determineAffectedTableColumnIndexes(
          tableState,
          computedTableState,
          selectedForm.latestFormDefinitionVersion.definition,
          newFieldKeyToTableColumnIndex
        );
        if (Object.values(affectedColumnIndexes).some((columnIndexes) => columnIndexes.length)) {
          const warnings = makeAffectedTableColumnWarnings(
            tableState,
            computedTableState,
            affectedColumnIndexes
          );
          confirm(
            'Switch to form?',
            <div>
              Changing to the selected form will cause some data to be lost:
              <ul>{warnings}</ul>
            </div>,
            ButtonStyle.PRIMARY,
            {
              enableRedesign: true,
              onSubmit: () => {
                setTaskTableOrFormToggleGroup?.(selectedOption);
                setSelectedForm(selectedForm);
                const fixedTableState = applyAffectedTableColumnFixes(
                  tableState,
                  computedTableState,
                  affectedColumnIndexes
                );
                updateHandler(fixedTableState[CREATE_TASK_ACTION.identifier]);
              },
            }
          );
          return;
        }
      } else if (selectedOption === TableOrFormRenderOptions.TABLE && tableState) {
        // When we switch back to the table view, we need to restore the columns' isRequired field
        // because the form view overwrites it. We don't want to have configurations on the form
        // apply to the table view.
        const restoredStructuredTableState = produce(tableState[CREATE_TASK_ACTION.identifier], (draft) => {
          for (let columnIndex = 0; columnIndex < draft.columns.length; columnIndex++) {
            draft.columns[columnIndex].isRequired =
              initialStructuredTable.current.columns[columnIndex].isRequired;
          }
        });
        updateHandler(restoredStructuredTableState);
      }
      setTaskTableOrFormToggleGroup?.(selectedOption);
      setSelectedForm(selectedForm);
    },
    [
      computedTableState,
      setSelectedForm,
      setTaskTableOrFormToggleGroup,
      structuredTable,
      tableState,
      taskSchema.id,
      updateHandler,
    ]
  );

  return (
    <div>
      {setTaskTableOrFormToggleGroup !== undefined && showToggleGroup && (
        <div className={css(styles.taskFrameHeader)}>
          <div>Tasks</div>
          {isWorkflowCreateTaskFormsEnabled() ? (
            <TableFormSelector
              selectedOption={taskTableOrFormToggleGroup}
              selectedForm={selectedForm}
              forms={forms || []}
              onTableFormSelect={onTableFormSelect}
              tableLockEnabled={tableLockEnabled}
            />
          ) : (
            <ToggleGroup<TableOrFormRenderOptionsType>
              options={
                tableLockEnabled
                  ? TABLE_LOCKED_TABLE_FORM_TOGGLE_BUTTON_OPTIONS
                  : TABLE_FORM_TOGGLE_BUTTON_OPTIONS
              }
              selection={taskTableOrFormToggleGroup}
              onChangeSelection={setTaskTableOrFormToggleGroup}
              size={ButtonSize.MEDIUM}
              disabled={tableLockEnabled}
            />
          )}
        </div>
      )}
      <div className={showToggleGroup ? css(styles.taskFrameContent) : ''}>
        {showConfiguredForm && selectedForm !== undefined ? (
          <EmbeddedCustomForm
            tableState={tableState!}
            fieldKeyToTableColumnIndex={fieldKeyToTableColumnIndex!}
            onComputedTableChanged={onComputedTableChanged}
            updateHandler={updateHandler}
            selectedForm={selectedForm}
          />
        ) : (
          <StructuredTableOrFormOutsideNotebook
            structuredTable={structuredTable}
            cid={structuredTable.cid}
            updateHandler={updateHandler}
            onComputedTableChanged={onComputedTableChanged}
            locked={locked}
            hasPrimarySamples={false}
            includeUnsubmittedStatus={false}
            shouldShowAs={taskTableOrFormToggleGroup}
            isCollapsible={false}
            enableFormulas={true}
            maximumHeight={!isOutputTable ? MaximumTableHeight.AUTO_FIXED : MaximumTableHeight.FULL}
          />
        )}
      </div>
    </div>
  );
}

export function CreateProcessForm(props: CreateProcessFormProps): JSX.Element {
  const hasOutputSchema = props.taskSchema.outputSchema != null;
  const outputTable = hasOutputSchema
    ? createOutputTableWithPlaceholderRowFromSchema({
        name: props.taskSchema.name,
        outputSchema: props.taskSchema.outputSchema!,
        tableLocation: ProcessTableLocation.CREATE_MODAL,
        selectorsById: props.selectorsById,
      })
    : null;
  const forms = props.configuredForms?.filter(
    (formOrInaccessible): formOrInaccessible is FormDefinition => !isInaccessible(formOrInaccessible)
  );
  return (
    <>
      <Row>
        <Col sm={6}>
          <Label>{DEFAULT_PROJECT_FOLDER_LABEL_TEXT}</Label>
          <FolderSelector
            enableRedesign={true}
            showInventory={false}
            showNotebook={true}
            value={props.folderId}
            error={props.folderId == null ? 'Required' : undefined}
            onChange={(e) => {
              const selectedFolderId = e.target.value;
              props.setFolderId(selectedFolderId);
              analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_CREATION_FOLDER, {
                process_client_origin: props.taskSchema.clientOrigin,
                old_value: props.folderId,
                new_value: selectedFolderId,
              });
            }}
            unsetOnFetchFail={true}
          />
        </Col>
        <Col sm={6} className={css(styles.watcherField)}>
          <Label>Watchers</Label>
          <EntityInputWithSearch
            value={props.watchers}
            placeholder="Set watchers"
            size={TagsInputSize.SMALL}
            onChange={props.setWatchers}
            allowEmails={true}
            allowTeams={true}
            allowOrganizations={false}
            shouldAllowMultiple={true}
            enableNewEntityInput={true}
          />
        </Col>
      </Row>
      <Row className={css(styles.instructionsContainer)}>
        <Col sm={12}>
          <Label>Instructions</Label>
          <TaskGroupInstructions
            lines={DefaultTaskGroupInstructionsLines.CREATE_MODAL}
            textValue={props.taskGroupInstructions}
            actionOrigin={ActionOrigin.WORKFLOWS_TASK_CREATION_MODAL_INSTRUCTIONS_HEADER}
            clientOrigin={props.taskSchema.clientOrigin}
          />
        </Col>
      </Row>
      {props.taskTable && (
        <Row className={css(styles.taskTable)}>
          <EmbeddedStructuredTable
            taskSchema={props.taskSchema}
            structuredTable={props.taskTable}
            updateHandler={props.setTaskTable}
            computedTable={props.computedTaskTable}
            onComputedTableChanged={props.setComputedTaskTable}
            taskTableOrFormToggleGroup={props.taskTableOrFormToggleGroup}
            setTaskTableOrFormToggleGroup={props.setTaskTableOrFormToggleGroup}
            selectedForm={props.selectedForm ?? forms?.[0]}
            setSelectedForm={props.setSelectedForm}
            forms={forms}
          />
        </Row>
      )}
      {outputTable && (
        <Row className={css(styles.outputTable)}>
          <EmbeddedStructuredTable
            taskSchema={props.taskSchema}
            structuredTable={outputTable}
            updateHandler={noop}
            taskTableOrFormToggleGroup={TableOrFormRenderOptions.TABLE}
            locked={true}
            setSelectedForm={noop}
            isOutputTable={true}
          />
        </Row>
      )}
    </>
  );
}

function CreateProcessModalContent(props: CreateProcessModalContentProps): JSX.Element | null {
  if (
    props.schemaQueryResult.type === QueryResultType.ERROR ||
    props.selectorsByIdResult.type === AsyncStatus.ERROR
  ) {
    return null;
  } else if (
    props.schemaQueryResult.type === QueryResultType.PENDING ||
    props.selectorsByIdResult.type === AsyncStatus.LOADING
  ) {
    return <CenteredHorizontalThrobber />;
  } else if (props.schemaQueryResult.inaccessibleInfo) {
    return <div>Inaccessible</div>;
  } else if (props.schemaQueryResult.taskSchema == null) {
    return <div>Not found</div>;
  } else if (
    props.schemaQueryResult.taskSchema.executionType === ProcessExecutionType.FLOWCHART &&
    (props.schemaQueryResult.taskSchema.flowchartConfig?.currentFlowchartConfigVersion == null ||
      isInaccessible(props.schemaQueryResult.taskSchema.flowchartConfig.currentFlowchartConfigVersion))
  ) {
    return <div>Please contact your administrator to configure the flowchart for this task schema.</div>;
  }
  return (
    <CreateProcessForm
      taskSchema={props.schemaQueryResult.taskSchema}
      folderId={props.folderId}
      selectorsById={props.selectorsByIdResult.selectorsById!}
      setFolderId={props.setFolderId}
      watchers={props.watchers}
      setWatchers={props.setWatchers}
      taskGroupInstructions={props.taskGroupInstructions}
      taskTable={props.taskTable}
      setTaskTable={props.setTaskTable}
      computedTaskTable={props.computedTaskTable}
      setComputedTaskTable={props.setComputedTaskTable}
      taskTableOrFormToggleGroup={props.taskTableOrFormToggleGroup}
      setTaskTableOrFormToggleGroup={props.setTaskTableOrFormToggleGroup}
      configuredForms={props.schemaQueryResult.configuredForms}
      selectedForm={props.selectedForm}
      setSelectedForm={props.setSelectedForm}
    />
  );
}

function ShowFlowchartToggle(props: {
  isFlowchartVisible: boolean;
  onChangeIsFlowchartVisible: (isFlowchartVisible: boolean) => void;
}): JSX.Element {
  if (props.isFlowchartVisible) {
    return (
      <Tooltip text="Hide the flowchart from view">
        <Button
          buttonStyle={ButtonStyle.TERTIARY_WHITE}
          size={ButtonSize.SMALL}
          icon="triangle-right"
          onClick={() => {
            props.onChangeIsFlowchartVisible(!props.isFlowchartVisible);
            analytics.trackEvent(
              WORKFLOWS_ANALYTICS_ACTIONS.HID_FLOWCHART_PANE_IN_TASK_GROUP_CREATION_MODAL,
              {
                actionOrigin: ActionOrigin.WORKFLOWS_TASK_CREATION_MODAL,
                actionMethod: ActionMethod.BUTTON_CLICK,
              }
            );
          }}
          dataTestAttrs={{
            component: 'ShowFlowchartToggle',
            element: 'hideFlowchartButton',
          }}
        />
      </Tooltip>
    );
  } else {
    return (
      <Button
        buttonStyle={ButtonStyle.TRANSPARENT}
        size={ButtonSize.SMALL}
        onClick={() => {
          props.onChangeIsFlowchartVisible(!props.isFlowchartVisible);
          analytics.trackEvent(
            WORKFLOWS_ANALYTICS_ACTIONS.EXPANDED_FLOWCHART_PANE_IN_TASK_GROUP_CREATION_MODAL,
            {
              actionOrigin: ActionOrigin.WORKFLOWS_TASK_CREATION_MODAL,
              actionMethod: ActionMethod.BUTTON_CLICK,
            }
          );
        }}
        className={css(styles.showFlowchartButton)}
        dataTestAttrs={{
          component: 'ShowFlowchartToggle',
          element: 'showFlowchartButton',
        }}
      >
        <BenchlingIcon icon="workflow" />
        &nbsp;Show flowchart
      </Button>
    );
  }
}

/* @package */
export enum FlowchartPaneState {
  NO_FLOWCHART = 'NO_FLOWCHART',
  HIDDEN = 'HIDDEN',
  VISIBLE = 'VISIBLE',
  EDITING = 'EDITING',
}

const _PROCESSES_SHOW_FLOWCHART_PANE_FOR_FLOWCHART_TASK_GROUP_KEY =
  '_PROCESSES_SHOW_FLOWCHART_PANE_FOR_FLOWCHART_TASK_GROUP';

function setShowFlowchartPaneForFlowchartTaskGroupPreference(showFlowchartPane: boolean): void {
  preferences.set(_PROCESSES_SHOW_FLOWCHART_PANE_FOR_FLOWCHART_TASK_GROUP_KEY, showFlowchartPane);
}

function getDefaultFlowchartPaneStateFromPreference(): FlowchartPaneState {
  const preference = preferences.get(_PROCESSES_SHOW_FLOWCHART_PANE_FOR_FLOWCHART_TASK_GROUP_KEY);
  if (preference == null) {
    return FlowchartPaneState.VISIBLE;
  }
  return preference ? FlowchartPaneState.VISIBLE : FlowchartPaneState.HIDDEN;
}

interface CreateProcessModalPanesProps {
  flowchartPaneState: FlowchartPaneState;
  setFlowchartPaneState: (flowchartPaneState: FlowchartPaneState) => void;
  schemaQueryResult: ProcessQueryResult;
  selectorsByIdResult: SelectorsByIdResult;
  flowchart: ProcessInteractiveFlowchart | null;
  setFlowchart: (flowchart: ProcessInteractiveFlowchart | null) => void;
  initialFlowchart: ProcessInteractiveFlowchart | null;
  setInitialFlowchart: (flowchart: ProcessInteractiveFlowchart | null) => void;
  setSchemaMapperConfigs: (schemaMapperConfigs: Array<SchemaMapperConfig>) => void;
  folderId: string | null;
  setFolderId: (folderId: string | null) => void;
  watchers: Array<EntityWithTextAndSuspended>;
  setWatchers: (watchers: Array<EntityWithTextAndSuspended>) => void;
  taskGroupInstructions: string | null;
  setTaskGroupInstructions: (taskGroupInstructions: string | null) => void;
  taskTable: ProcessTaskStructuredTable | undefined;
  setTaskTable: (table: ProcessTaskStructuredTable) => void;
  computedTaskTable: EvaluatedTable;
  setComputedTaskTable: (computedTable: EvaluatedTable) => void;
  taskTableOrFormToggleGroup: TableOrFormRenderOptionsType;
  setTaskTableOrFormToggleGroup: (taskTableOrFormToggleGroup: TableOrFormRenderOptionsType) => void;
  selectedForm?: FormDefinition;
  setSelectedForm: (form: FormDefinition | undefined) => void;
  analyticsEventGroupId: string;
  actionOrigin: string;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  flowchartReadinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  onFlowchartEditBlockingErrorsChange: (hasBlocking: boolean) => void;
}

function CreateProcessModalPanes(props: CreateProcessModalPanesProps): JSX.Element {
  const taskSchema = props.schemaQueryResult.taskSchema;
  const flowchartConfigVersion = taskSchema?.flowchartConfig?.currentFlowchartConfigVersion;
  const isFlowchartModifiableOnCreation =
    flowchartConfigVersion != null && !isInaccessible(flowchartConfigVersion)
      ? flowchartConfigVersion.isFlowchartModifiableOnCreation
      : true;

  const taskCreationPane = (
    <CreateProcessModalContent
      schemaQueryResult={props.schemaQueryResult}
      selectorsByIdResult={props.selectorsByIdResult}
      folderId={props.folderId}
      setFolderId={props.setFolderId}
      watchers={props.watchers}
      setWatchers={props.setWatchers}
      taskGroupInstructions={props.taskGroupInstructions}
      taskTable={props.taskTable}
      setTaskTable={props.setTaskTable}
      computedTaskTable={props.computedTaskTable}
      setComputedTaskTable={props.setComputedTaskTable}
      taskTableOrFormToggleGroup={props.taskTableOrFormToggleGroup}
      setTaskTableOrFormToggleGroup={props.setTaskTableOrFormToggleGroup}
      selectedForm={props.selectedForm}
      setSelectedForm={props.setSelectedForm}
    />
  );

  if (props.flowchartPaneState === FlowchartPaneState.NO_FLOWCHART) {
    return taskCreationPane;
  }

  // We use CSS classes to control the visibility of the task creation pane and the flowchart pane.
  // This is to avoid unmounting and remounting the various components when toggling between the different views
  // which causes the components to lose their state.
  let taskColumnProps: {sm: number; className: string};
  let flowchartColumnProps: {sm: number; className: string};
  switch (props.flowchartPaneState) {
    case FlowchartPaneState.VISIBLE:
      taskColumnProps = {
        sm: 6,
        className: css(styles.dualPaneMainFormColumnWrapper),
      };
      flowchartColumnProps = {
        sm: 6,
        className: css(styles.dualPaneFlowchartColumnWrapper),
      };
      break;
    case FlowchartPaneState.EDITING:
      taskColumnProps = {
        sm: 0,
        className: css(styles.hiddenColumnWrapper),
      };
      flowchartColumnProps = {
        sm: 12,
        className: css(styles.dualPaneFlowchartColumnWrapper),
      };
      break;
    case FlowchartPaneState.HIDDEN:
      taskColumnProps = {
        sm: 12,
        className: css(styles.dualPaneMainFormColumnWrapper),
      };
      flowchartColumnProps = {
        sm: 0,
        className: css(styles.hiddenColumnWrapper),
      };
      break;
    default:
      assert(false, `Unexpected flowchart pane state ${assertNever(props.flowchartPaneState)}`);
      taskColumnProps = {sm: 6, className: ''};
      flowchartColumnProps = {sm: 6, className: ''};
  }

  return (
    <Row className={css(styles.dualPaneRowWrapper)}>
      <Col
        dataTestAttrs={{component: 'TaskCreationPaneWrapper', element: 'columnWrapper'}}
        {...taskColumnProps}
      >
        <div className={classNames('u-lr-split', css(styles.titleBar))}>
          <h5>Task details</h5>
          <ShowFlowchartToggle
            isFlowchartVisible={props.flowchartPaneState === FlowchartPaneState.VISIBLE}
            onChangeIsFlowchartVisible={(isFlowchartVisible) => {
              setShowFlowchartPaneForFlowchartTaskGroupPreference(isFlowchartVisible);
              props.setFlowchartPaneState(
                isFlowchartVisible ? FlowchartPaneState.VISIBLE : FlowchartPaneState.HIDDEN
              );
            }}
          />
        </div>
        <div>{taskCreationPane}</div>
      </Col>
      <Col
        dataTestAttrs={{component: 'FlowchartPaneWrapper', element: 'columnWrapper'}}
        {...flowchartColumnProps}
      >
        {BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG ? (
          <ImprovedCreateProcessFlowchartPane
            flowchartConfigId={props.schemaQueryResult.taskSchema!.flowchartConfig!.id}
            isEditing={props.flowchartPaneState === FlowchartPaneState.EDITING}
            eventGroupId={props.analyticsEventGroupId}
            taskSchemaApiId={props.schemaQueryResult.taskSchema!.id}
            draftState={props.draftState}
            setDraftState={props.setDraftState}
            readinessSnapshot={props.flowchartReadinessSnapshot}
            onFlowchartEditBlockingErrorsChange={props.onFlowchartEditBlockingErrorsChange}
            isFlowchartModifiableOnCreation={isFlowchartModifiableOnCreation}
            onEditStatusChange={(isEditing) => {
              const wasPreviouslyEditing = props.flowchartPaneState === FlowchartPaneState.EDITING;
              if (isEditing === wasPreviouslyEditing) {
                return;
              }
              props.setFlowchartPaneState(
                isEditing ? FlowchartPaneState.EDITING : FlowchartPaneState.VISIBLE
              );
            }}
          />
        ) : (
          <CreateProcessGraphBuilderPane
            flowchart={props.flowchart}
            flowchartSchema={props.schemaQueryResult.taskSchema!}
            onInitializeFlowchart={(flowchart, schemaMapperConfigs) => {
              if (props.initialFlowchart != null) {
                return;
              }
              props.setFlowchart(flowchart);
              props.setInitialFlowchart(flowchart);
              props.setSchemaMapperConfigs(schemaMapperConfigs);
            }}
            onChangeFlowchart={(flowchart) => {
              props.setFlowchart(flowchart);
            }}
            onChangeEditStatus={(isEditing) => {
              const wasPreviouslyEditing = props.flowchartPaneState === FlowchartPaneState.EDITING;
              if (isEditing === wasPreviouslyEditing) {
                return;
              }
              props.setFlowchartPaneState(
                isEditing ? FlowchartPaneState.EDITING : FlowchartPaneState.VISIBLE
              );
            }}
            modalId={props.analyticsEventGroupId}
            actionOrigin={props.actionOrigin}
            eventGroupId={props.analyticsEventGroupId}
          />
        )}
      </Col>
    </Row>
  );
}

/**
 * State management for ImprovedCreateProcessFlowchartPane.
 */
function useCreateProcessFlowchartPaneState(args: {
  flowchartConfigIdForAcmDraft: string | null;
  analyticsEventGroupId: string;
  flowchartPaneState: FlowchartPaneState;
  setFlowchartPaneState: Dispatch<SetStateAction<FlowchartPaneState>>;
  taskSchema: ProcessQueryResult['taskSchema'];
}): {
  flowchartEditHasBlockingErrors: boolean;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  flowchartReadinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  hasSavedFlowchartEdits: boolean;
  handleFlowchartEditBlockingErrorsChange: (hasBlocking: boolean) => void;
  handleFlowchartEditCancel: () => void;
  handleFlowchartEditSave: () => void;
} {
  const {
    flowchartConfigIdForAcmDraft,
    analyticsEventGroupId,
    flowchartPaneState,
    setFlowchartPaneState,
    taskSchema,
  } = args;
  const [flowchartEditHasBlockingErrors, setFlowchartEditHasBlockingErrors] = useState(false);
  const [hasSavedFlowchartEdits, setHasSavedFlowchartEdits] = useState(false);
  // Snapshot of the draft taken every time the user clicks Save in the flowchart pane. On Cancel
  // we restore this so canceling a later edit session doesn't drop edits from an earlier saved one.
  const [lastSavedDraftState, setLastSavedDraftState] = useState<FlowchartConfigWithMetadata | null>(null);

  const {draftState, setDraftState, readinessSnapshot} = useExecutionFlowchartDraftStateForConfig({
    flowchartConfigId: flowchartConfigIdForAcmDraft,
    editorMode:
      flowchartPaneState === FlowchartPaneState.EDITING
        ? FlowchartEditorMode.ACM_EDITOR
        : FlowchartEditorMode.EXECUTION_VIEWER,
  });

  const handleFlowchartEditBlockingErrorsChange = useCallback((hasBlocking: boolean): void => {
    setFlowchartEditHasBlockingErrors(hasBlocking);
  }, []);

  const exitFlowchartEditMode = useCallback((): void => {
    setFlowchartPaneState(FlowchartPaneState.VISIBLE);
  }, [setFlowchartPaneState]);

  const taskSchemaApiIdForFlowchartAnalytics = taskSchema?.id ?? '';

  const handleFlowchartEditCancel = useCallback((): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: analyticsEventGroupId,
      action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
      action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
      task_schema_api_id: taskSchemaApiIdForFlowchartAnalytics,
      close_type: CloseType.CANCEL,
      error_types: [],
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    // Roll back to the last saved snapshot (null if the user has not saved this modal session).
    setDraftState(lastSavedDraftState);
    exitFlowchartEditMode();
  }, [
    analyticsEventGroupId,
    exitFlowchartEditMode,
    lastSavedDraftState,
    setDraftState,
    taskSchemaApiIdForFlowchartAnalytics,
  ]);

  const handleFlowchartEditSave = useCallback((): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: analyticsEventGroupId,
      action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
      action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
      task_schema_api_id: taskSchemaApiIdForFlowchartAnalytics,
      error_types: [],
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    setLastSavedDraftState(draftState);
    setHasSavedFlowchartEdits(true);
    exitFlowchartEditMode();
  }, [analyticsEventGroupId, draftState, exitFlowchartEditMode, taskSchemaApiIdForFlowchartAnalytics]);

  return {
    flowchartEditHasBlockingErrors,
    draftState,
    setDraftState,
    flowchartReadinessSnapshot: readinessSnapshot,
    hasSavedFlowchartEdits,
    handleFlowchartEditBlockingErrorsChange,
    handleFlowchartEditCancel,
    handleFlowchartEditSave,
  };
}

export default function CreateProcessModal(props: CreateProcessModalProps): JSX.Element {
  const {processTaskSchemaId} = props;
  const schemaQueryResult = useProcessTaskSchema(processTaskSchemaId);
  const selectorsByIdResult = useSelectorsByIdWithStatus();
  const [folderId, setFolderId] = useState(props.initialFolderId);
  const [watchers, setWatchers] = useState<Array<EntityWithTextAndSuspended>>([EntityStore.currentUser()]);
  const [taskGroupInstructions, setTaskGroupInstructions] = useState(props.taskGroupInstructions);
  const [computedTaskTable, setComputedTaskTable] = useState<EvaluatedTable>([[]]);
  const [taskTable, setTaskTable] = useState<ProcessTaskStructuredTable | undefined>(
    props.taskTableWithPropagatedContent
  );
  const [initialFlowchart, setInitialFlowchart] = useState<ProcessInteractiveFlowchart | null>(null);
  const [schemaMapperConfigs, setSchemaMapperConfigs] = useState<Array<SchemaMapperConfig> | null>(null);
  const [flowchart, setFlowchart] = useState<ProcessInteractiveFlowchart | null>(null);

  const [flowchartPaneState, setFlowchartPaneState] = useState(FlowchartPaneState.NO_FLOWCHART);

  const flowchartEligibility = useMemo(
    () => getCreateProcessModalFlowchartEligibility(schemaQueryResult.taskSchema),
    [schemaQueryResult.taskSchema]
  );

  const {
    flowchartEditHasBlockingErrors,
    draftState,
    setDraftState,
    flowchartReadinessSnapshot,
    hasSavedFlowchartEdits,
    handleFlowchartEditBlockingErrorsChange,
    handleFlowchartEditCancel,
    handleFlowchartEditSave,
  } = useCreateProcessFlowchartPaneState({
    flowchartConfigIdForAcmDraft: flowchartEligibility.flowchartConfigIdForAcmDraft,
    analyticsEventGroupId: props.analyticsEventGroupId,
    flowchartPaneState,
    setFlowchartPaneState,
    taskSchema: schemaQueryResult.taskSchema,
  });

  const initialViewPreference = useRef<CreateTaskModalViewPreference>(
    getInitialCreateTaskModalViewPreference(processTaskSchemaId, taskTable)
  );
  const [initialViewMode, initialSelectedFormId] = initialViewPreference.current;

  const defaultTaskCreationView = TableOrFormRenderOptionsToAnalyticsType[initialViewPreference.current[0]];

  const [taskTableOrFormToggleGroup, setTaskTableOrFormToggleGroup] =
    useState<TableOrFormRenderOptionsType>(initialViewMode);

  const [selectedForm, setSelectedForm] = useState<FormDefinition | undefined>();
  // set the selected form when we have our forms (if it's still selectable)
  useEffect(() => {
    setSelectedForm(
      initialSelectedFormId
        ? schemaQueryResult.configuredForms?.find(
            (form): form is FormDefinition => !isInaccessible(form) && form.id === initialSelectedFormId
          )
        : undefined
    );
  }, [initialSelectedFormId, schemaQueryResult.configuredForms]);

  useEffect(() => {
    if (
      schemaQueryResult.taskSchema &&
      selectorsByIdResult.type === AsyncStatus.SUCCESS &&
      props.taskTableWithPropagatedContent == null
    ) {
      setTaskTable(
        createEmptyTaskTableFromSchema({
          name: schemaQueryResult.taskSchema.name,
          taskSchema: schemaQueryResult.taskSchema,
          tableLocation: ProcessTableLocation.CREATE_MODAL,
          locked: false,
          selectorsById: selectorsByIdResult.selectorsById!,
          defaultValuesByColumnType: props.defaultTaskValuesByColumnType,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaQueryResult.taskSchema, selectorsByIdResult.type]);

  useEffect(() => {
    if (schemaQueryResult.taskSchema && props.initialFolderId == null) {
      let initialFolderId = null;
      if (
        schemaQueryResult.taskSchema.defaultCreationFolderOrInaccessible != null &&
        !isInaccessible(schemaQueryResult.taskSchema.defaultCreationFolderOrInaccessible)
      ) {
        initialFolderId = schemaQueryResult.taskSchema.defaultCreationFolderOrInaccessible.id;
      } else {
        initialFolderId = FolderDefaults.get(FolderDefaultsKey.PROCESS) ?? null;
      }
      if (initialFolderId != null) {
        setFolderId(initialFolderId);
      }
    }
  }, [schemaQueryResult.taskSchema, props.initialFolderId]);

  useEffect(() => {
    if (schemaQueryResult.taskSchema && props.taskGroupInstructions == null) {
      const taskGroupInstructions = schemaQueryResult.taskSchema.taskGroupInstructions;
      if (taskGroupInstructions != null) {
        setTaskGroupInstructions(taskGroupInstructions);
      }
    }
  }, [schemaQueryResult.taskSchema, props.taskGroupInstructions]);

  useEffect(() => {
    const defaultFlowchartPaneState = flowchartEligibility.canShowFlowchartPane
      ? getDefaultFlowchartPaneStateFromPreference()
      : FlowchartPaneState.NO_FLOWCHART;
    setFlowchartPaneState(defaultFlowchartPaneState);
  }, [flowchartEligibility.canShowFlowchartPane]);

  const handleTaskTableOrFormToggle = (taskTableOrFormToggleGroup: TableOrFormRenderOptionsType): void => {
    setTaskTableOrFormToggleGroup(taskTableOrFormToggleGroup);

    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ADJUSTED_VIEW_TASK_CREATION, {
      process_client_origin: schemaQueryResult.taskSchema?.clientOrigin || null,
      event_group_id: props.analyticsEventGroupId,
      action_origin: ActionOrigin.WORKFLOWS_TASK_CREATION_MODAL_WORKFLOW_TASK_TABLE,
      adjustment_type:
        taskTableOrFormToggleGroup === TableOrFormRenderOptions.TABLE
          ? TableOrFormAdjustmentType.SWITCHED_TO_TASK_TABLE_VIEW
          : TableOrFormAdjustmentType.SWITCHED_TO_TASK_FORM_VIEW,
      num_tasks: taskTable ? taskTable.rows.length : 0,
    });
  };

  const hasBlockingExecutionFlowchartErrors = useMemo((): boolean => {
    if (
      !BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG ||
      draftState == null ||
      flowchartReadinessSnapshot.data == null ||
      flowchartEligibility.flowchartConfigIdForAcmDraft == null
    ) {
      return false;
    }
    const {data: flowchartData} = flowchartReadinessSnapshot;
    const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(draftState.metadata.flowchartTypeName);
    const executionValidationErrors = validateFlowchartDataForExecution(
      draftState.staticFlowchart,
      flowchartEligibility.flowchartConfigIdForAcmDraft,
      draftState.metadata,
      flowchartData.rootTaskSchema,
      flowchartData.hasChainedRouters,
      plugin,
      undefined,
      FlowchartEditorMode.ACM_EDITOR
    );
    return hasBlockingExecutionErrors(executionValidationErrors);
  }, [draftState, flowchartEligibility.flowchartConfigIdForAcmDraft, flowchartReadinessSnapshot]);

  const isFlowchartValidForApi = ((): boolean => {
    if (schemaQueryResult.taskSchema?.executionType !== ProcessExecutionType.FLOWCHART) {
      return true;
    } else if (schemaQueryResult.taskSchema.flowchartConfig == null) {
      return false;
    } else if (BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG) {
      if (flowchartEligibility.isFlowchartTooLarge) {
        return true;
      }
      if (!flowchartEligibility.canShowFlowchartPane) {
        return false;
      }
      return !hasBlockingExecutionFlowchartErrors;
    } else {
      return flowchart != null || flowchartEligibility.isFlowchartTooLarge;
    }
  })();

  const isSubmitButtonDisabled =
    folderId == null ||
    doesEvaluatedTableHaveAnyErrors(computedTaskTable, EVALUATED_TABLE_ERROR_OPTIONS) ||
    schemaQueryResult.type !== QueryResultType.SUCCESS ||
    !isFlowchartValidForApi ||
    flowchartPaneState === FlowchartPaneState.EDITING;

  const handleSubmit = async (): Promise<void> => {
    FolderDefaults.set(FolderDefaultsKey.PROCESS, folderId!);

    let flowchartSpec = null;
    let executionFlowchartClientId: string | null = null;
    if (BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG) {
      // Improved ACM editor: pull the draft from the new horizontal flowchart editor when the
      // user has saved edits in the flowchart pane, then convert it to the same network spec
      // shape the legacy editor produces so the rest of the create flow is unchanged.
      if (hasSavedFlowchartEdits && draftState != null) {
        // Saving in the flowchart pane requires the editor to have loaded; the readiness snapshot
        // is populated as part of that load, so by the time hasSavedFlowchartEdits is true the
        // persisted mapper configs are available.
        assert(
          flowchartReadinessSnapshot.data != null,
          'Expected flowchart readiness data to be loaded when submitting saved ACM flowchart edits'
        );
        executionFlowchartClientId = stubbableFunctions.makeClientId();
        flowchartSpec = buildExecutionFlowchartSpecFromDraftState({
          draftState,
          flowchartClientId: executionFlowchartClientId,
          persistedSchemaMapperConfigs: flowchartReadinessSnapshot.data.persistedSchemaMapperConfigs,
          specVariant: ExecutionFlowchartSpecVariant.ACM_CREATE,
          flowchartConfigVersionId: flowchartReadinessSnapshot.data.flowchartConfigVersionId,
        });
      }
    } else if (flowchart != null && areProcessInteractiveFlowchartsDifferent(flowchart, initialFlowchart)) {
      assert(schemaMapperConfigs != null, 'Expected schema mapper configs to be provided', {
        schemaQueryResult,
      });
      executionFlowchartClientId = stubbableFunctions.makeClientId();
      flowchartSpec = {
        ...convertProcessInteractiveFlowchartToFlowchartSpec(
          flowchart,
          schemaMapperConfigs,
          schemaQueryResult.taskSchema!
        ),
        clientId: executionFlowchartClientId,
      };
    }

    const tasks: Array<ProcessManagementDAO.CreateTaskForNewProcess> = taskTableToTasks({
      table: taskTable!,
      evaluatedTable: computedTaskTable,
      sourceType: 'create_modal',
      actionOrigin: ActionOrigin.WORKFLOWS_TASK_CREATION_MODAL_WORKFLOW_TASK_TABLE,
      actionMethod: ActionMethod.BUTTON_CLICK,
    });
    if (executionFlowchartClientId != null) {
      for (const task of tasks) {
        task.executionFlowchartClientId = executionFlowchartClientId;
      }
    }

    const actionOrigin = getActionOrigin();
    const profileEventId = uuid.v4();

    const profilingMetadata = createProcessEventMetadata({
      numItems: tasks.length,
      numTasks: tasks.length,
      actionOrigin,
    });

    logProfilerTimingEvent(
      {...PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT, id: profileEventId},
      profilingMetadata
    );

    const process = await createProcess({
      schemaId: schemaQueryResult.taskSchema!.id,
      folderId: folderId!,
      watcherIds: watchers.map(({id}) => id),
      tasks,
      sourceType: props.sourceType,
      actionOrigin,
      actionMethod: ActionMethod.BUTTON_CLICK,
      ...(flowchartSpec != null ? {flowcharts: [flowchartSpec]} : {}),
      creationModalId: props.analyticsEventGroupId,
    });

    props.onRequestClose();

    logProfilerTimingEvent(
      {...PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT, id: profileEventId},
      profilingMetadata
    );

    const actionMethod =
      taskTableOrFormToggleGroup === TableOrFormRenderOptions.TABLE
        ? ActionMethod.STRUCTURED_TABLE_SUBMISSION
        : ActionMethod.STRUCTURED_TABLE_FORM_SUBMISSION;

    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.analyticsEventGroupId,
      action_origin: actionOrigin,
      action_method: actionMethod,
      default_task_creation_view: defaultTaskCreationView,
      modal_type: CREATE_PROCESS_MODAL_TYPE,
    });
    BenchlingUtil.navigate(null, ProcessManagementDAO.processDetailsURL(process.id));
  };

  const closeModal = (): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.analyticsEventGroupId,
      action_origin: getActionOrigin(),
      default_task_creation_view: defaultTaskCreationView,
      modal_type: CREATE_PROCESS_MODAL_TYPE,
    });
    props.onRequestClose();
  };

  function getActionOrigin(): ActionOrigin {
    switch (props.sourceType) {
      case 'dashboard': {
        return ActionOrigin.WORKFLOWS_SIDEPANEL_CREATE;
      }
      case 'global_create': {
        return ActionOrigin.SIDEPANEL_GLOBAL_CREATE;
      }
      default: {
        return ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE;
      }
    }
  }

  const footerMessage = ((): string | undefined => {
    const tooLargeFooterMessage = 'The flowchart for this schema is too large to display.';
    if (flowchartEligibility.isFlowchartTooLarge) {
      return props.footerMessage != null
        ? `${props.footerMessage}\n${tooLargeFooterMessage}`
        : tooLargeFooterMessage;
    }
    return props.footerMessage;
  })();

  const createProcessModalButtons: Array<ModalButton> = ((): Array<ModalButton> => {
    if (
      BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG &&
      flowchartPaneState === FlowchartPaneState.EDITING
    ) {
      return [
        {
          text: 'Cancel',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: handleFlowchartEditCancel,
          dataTestAttrs: {component: 'CreateProcessModal', key: ModalButtonKey.CANCEL},
        },
        {
          text: 'Save edits',
          style: ButtonStyle.SECONDARY,
          disabled: flowchartEditHasBlockingErrors,
          onClick: handleFlowchartEditSave,
          dataTestAttrs: {component: 'CreateProcessModal', key: ModalButtonKey.DONE},
        },
      ];
    }
    if (flowchartPaneState === FlowchartPaneState.EDITING) {
      return [];
    }
    return [
      {
        text: 'Create',
        style: ButtonStyle.PRIMARY,
        disabled: isSubmitButtonDisabled,
        onClick: handleSubmit,
        dataTestAttrs: {component: 'CreateProcessModal', key: ModalButtonKey.CREATE},
      },
    ];
  })();

  const sharedProps = {
    title: `Create task group: ${props.processTaskSchemaName}`,
    expandHeight: true,
    large: true,
    onRequestClose: closeModal,
    buttons: createProcessModalButtons,
    wrapInForm: false,
    footer: footerMessage != null ? <div className={css(styles.footerText)}>{footerMessage}</div> : null,
    modalKey: 'createProcess',
    rememberExpandedSetting: true,
  };

  const modalContents = (
    <CreateProcessModalPanes
      schemaQueryResult={schemaQueryResult}
      selectorsByIdResult={selectorsByIdResult}
      folderId={folderId}
      setFolderId={setFolderId}
      flowchart={flowchart}
      setFlowchart={setFlowchart}
      initialFlowchart={initialFlowchart}
      setInitialFlowchart={setInitialFlowchart}
      setSchemaMapperConfigs={setSchemaMapperConfigs}
      taskTable={taskTable}
      setTaskTable={setTaskTable}
      computedTaskTable={computedTaskTable}
      setComputedTaskTable={setComputedTaskTable}
      taskGroupInstructions={taskGroupInstructions}
      setTaskGroupInstructions={setTaskGroupInstructions}
      watchers={watchers}
      setWatchers={setWatchers}
      selectedForm={selectedForm}
      setSelectedForm={setSelectedForm}
      taskTableOrFormToggleGroup={taskTableOrFormToggleGroup}
      setTaskTableOrFormToggleGroup={handleTaskTableOrFormToggle}
      flowchartPaneState={flowchartPaneState}
      setFlowchartPaneState={setFlowchartPaneState}
      analyticsEventGroupId={props.analyticsEventGroupId}
      actionOrigin={getActionOrigin()}
      draftState={draftState}
      setDraftState={setDraftState}
      flowchartReadinessSnapshot={flowchartReadinessSnapshot}
      onFlowchartEditBlockingErrorsChange={handleFlowchartEditBlockingErrorsChange}
    />
  );

  if (flowchartPaneState !== FlowchartPaneState.NO_FLOWCHART) {
    return (
      <Modal {...sharedProps} extraLarge={true} fullHeight={true} bodyPadded={false} flexBody={false}>
        {modalContents}
      </Modal>
    );
  } else {
    return (
      <ExpandableModal
        {...sharedProps}
        large={true}
        bodyPadded={true}
        flexBody={true}
        modalKey="createProcess"
        rememberExpandedSetting={true}
      >
        {modalContents}
      </ExpandableModal>
    );
  }
}

const styles = StyleSheet.create({
  folderSelector: {
    width: '33%',
  },
  taskTable: {
    marginTop: '25px',
    marginRight: '16px',
    marginLeft: 0,
  },
  taskFrameHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    fontWeight: 'bold',
    alignItems: 'center',
  },
  taskFrameContent: {
    padding: '10px 0',
  },
  outputTable: {
    marginRight: '16px',
    marginLeft: 0,
  },
  footerText: {
    height: '36px',
    verticalAlign: 'middle',
    display: 'table-cell',
    textAlign: 'left',
  },
  watcherField: {
    paddingRight: '29px',
  },
  instructionsContainer: {
    marginTop: '16px',
  },
  dualPaneRowWrapper: {
    height: '100%',
    width: '100%',
    margin: 0,
  },
  dualPaneMainFormColumnWrapper: {
    padding: '0 16px 16px 16px 16px',
  },
  hiddenColumnWrapper: {
    display: 'none',
  },
  dualPaneFlowchartColumnWrapper: {
    height: '100%',
    borderLeft: `1px solid ${GRAY_LIGHT}`,
    background: GRAY_LIGHTER,
    padding: 0,
    overflow: 'hidden',
  },
  titleBar: {
    background: WHITE,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '20px 0',
    height: '40px',
    marginBottom: '8px',
  },
  showFlowchartButton: {
    color: BLUE_LIGHT,
    textDecoration: 'none',
  },
});
